import type { MessageAttachmentDto } from "@multi-chat/shared";

export function attachmentTypeName(attachment: MessageAttachmentDto): string {
  return attachment.attachmentType.toUpperCase();
}

export function isImageAttachment(attachment: MessageAttachmentDto): boolean {
  return attachmentTypeName(attachment) === "IMAGE";
}

export function isStickerAttachment(attachment: MessageAttachmentDto): boolean {
  return attachmentTypeName(attachment) === "STICKER";
}

/**
 * Stickers, plus Messenger GIF-like `image/gif` photos, use the generic sticker
 * <img> path so animation and alpha are preserved without a photo card.
 * WhatsApp photos stay on the image path (typically jpeg/png).
 */
export function usesStickerPresentation(attachment: MessageAttachmentDto): boolean {
  if (isStickerAttachment(attachment)) {
    return true;
  }
  return isImageAttachment(attachment) && attachment.mimeType?.trim().toLowerCase() === "image/gif";
}

export function isVideoAttachment(attachment: MessageAttachmentDto): boolean {
  return attachmentTypeName(attachment) === "VIDEO";
}

export function isAudioAttachment(attachment: MessageAttachmentDto): boolean {
  return attachmentTypeName(attachment) === "AUDIO";
}

/** WhatsApp Cloud API voice note: AUDIO + providerVoice, never a separate MessageType. */
export function isVoiceAttachment(attachment: MessageAttachmentDto): boolean {
  return isAudioAttachment(attachment) && attachment.providerVoice === true;
}

export function isDocumentAttachment(attachment: MessageAttachmentDto): boolean {
  return attachmentTypeName(attachment) === "DOCUMENT";
}

export function messageHasVoiceAttachment(
  attachments: MessageAttachmentDto[] | undefined,
): boolean {
  return attachments?.some(isVoiceAttachment) === true;
}

export function firstStickerAttachment(
  attachments: MessageAttachmentDto[] | undefined,
): MessageAttachmentDto | undefined {
  return attachments?.find(isStickerAttachment);
}

export function firstDocumentAttachment(
  attachments: MessageAttachmentDto[] | undefined,
): MessageAttachmentDto | undefined {
  return attachments?.find(isDocumentAttachment);
}
